import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { JWT_TOKEN, REFRESH_TOKEN, getLoginUrlWithRedirect } from "../constants/auth";
import {
  cookiesCleanup,
  saveRefreshToken,
  saveUserInfo,
  saveUserToken,
} from "../utils/authStorage";
import { ssoRefreshToken } from "../services/ssoAuthService";
import { logger } from "../utils/logger";
import { encodeUserInfo } from "../utils/userInfoEncoding";

interface AuthContextValue {
  isAuthenticated: boolean;
  isResolving: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(() => Boolean(Cookies.get(JWT_TOKEN)));
  const [isResolving, setIsResolving] = useState(() => !Cookies.get(JWT_TOKEN));

  useEffect(() => {
    if (Cookies.get(JWT_TOKEN)) {
      setIsResolving(false);
      return;
    }

    let cancelled = false;

    async function getAccessTokenSilently() {
      const refreshToken = Cookies.get(REFRESH_TOKEN);

      if (!refreshToken) {
        cookiesCleanup();
        window.location.href = getLoginUrlWithRedirect();
        return;
      }

      try {
        const data = await ssoRefreshToken({ refreshToken });
        if (data.status === 0) {
          saveUserToken(data.accessToken);
          saveRefreshToken(data.refreshToken, data.refreshTokenExpiresAt);

          const infoPayload = {
            applications: data.applications,
            operatorId: data.operatorId,
            name: data.name,
            location: data.location,
          };
          saveUserInfo(encodeUserInfo(infoPayload));

          if (!cancelled) {
            setIsAuthenticated(true);
            setIsResolving(false);
          }
        } else {
          cookiesCleanup();
          window.location.href = getLoginUrlWithRedirect();
        }
      } catch (error) {
        logger.error("Silent token refresh failed", error);
        cookiesCleanup();
        window.location.href = getLoginUrlWithRedirect();
      }
    }

    getAccessTokenSilently();

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo(() => ({ isAuthenticated, isResolving }), [isAuthenticated, isResolving]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuthContext(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}
